import * as React from 'react';
import styled from 'styled-components';

import { ReactComponent as IconDataset } from '@/images/icons/icon-dataset.svg';
import { ReactComponent as IconGroupedColumnChart } from '@/images/icons/icon-grouped-column-chart.svg';
import { ReactComponent as IconGroups } from '@/images/icons/icon-groups.svg';
import { ReactComponent as IconMarketing } from '@/images/icons/icon-marketing.svg';
import { ReactComponent as IconSales } from '@/images/icons/icon-sales.svg';

import { AppBuilderIcon } from '../../../icons/Icons';
import { ReactComponent as IconAbout } from '../../../images/icons/icon-about.svg';
import { ReactComponent as IconChangelog } from '../../../images/icons/icon-changelog.svg';
import { ReactComponent as IconCustomers } from '../../../images/icons/icon-customers.svg';
import { ReactComponent as IconDocs } from '../../../images/icons/icon-docs.svg';
import { ReactComponent as IconDownload } from '../../../images/icons/icon-download.svg';
import { ReactComponent as IconEnterprise } from '../../../images/icons/icon-enterprise.svg';
import { ReactComponent as IconExplore } from '../../../images/icons/icon-explore.svg';
import { ReactComponent as IconForTeams } from '../../../images/icons/icon-for-teams.svg';
import { ReactComponent as IconIntegrations } from '../../../images/icons/icon-integrations.svg';
import { ReactComponent as IconMagic } from '../../../images/icons/icon-magic.svg';
import { ReactComponent as IconNotebooks } from '../../../images/icons/icon-notebooks.svg';
import { ReactComponent as IconBlog } from '../../../images/icons/icon-our-blog.svg';
import { ReactComponent as IconPartners } from '../../../images/icons/icon-partners.svg';
import { ReactComponent as IconPin } from '../../../images/icons/icon-pin.svg';
import { ReactComponent as IconPricing } from '../../../images/icons/icon-pricing.svg';
import { ReactComponent as IconSemanticModels } from '../../../images/icons/icon-semantic-models.svg';
import { ReactComponent as IconTemplates } from '../../../images/icons/icon-templates.svg';
import { ReactComponent as IconExploratoryAnalysis } from '../../../images/icons/icon-use-case-exploratory-analysis.svg';
import { DOCS_LINKS, INTERNAL_LINKS } from '../../../utils';
import { Heading } from '../../design-system';

import { NavItem, type TextAlignment } from './NavItem';

const Main = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  gap: 3px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    margin-left: auto;
    justify-content: flex-start;
  }
`;

const NavOptions = styled.nav`
  display: flex;
  align-items: center;
  flex: 1 1 auto;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    padding: 0 10px 10px;

    ${Main} {
      flex: none;
      width: 100%;
      flex-direction: column;
      padding: 0;
      margin: 0;
      gap: 8px;

      > a {
        margin: 0;
      }
    }
  }
`;

const MobileMenu = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1 1 auto;
`;

const PurpleDivider = styled.div`
  margin: 5px 0;
  height: 1px;
  width: 100%;

  &::before {
    display: block;
    content: '';
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.borderColor.DEFAULT};
  }
`;

const HeadingDivider = styled(Heading).attrs({ renderAs: 'h5' })`
  padding-left: 14px;
  padding-top: 12px;
  width: 100%;
`;

export const NavContentsMobile: React.ComponentType = React.memo(() => {
  const navItemProps = {
    fill: true,
    partiallyActive: true,
    align: 'left' as TextAlignment
  };

  return (
    <NavOptions>
      <MobileMenu>
        <Main>
          <HeadingDivider renderAs="h5">Product</HeadingDivider>
          <NavItem
            {...navItemProps}
            icon={<IconNotebooks />}
            text="Notebooks"
            to={INTERNAL_LINKS.Notebooks}
          />
          <NavItem
            {...navItemProps}
            icon={<AppBuilderIcon />}
            text="Data apps"
            to={INTERNAL_LINKS.AppBuilder}
          />
          <NavItem
            {...navItemProps}
            icon={<IconExplore />}
            text="Explore"
            to={INTERNAL_LINKS.Explore}
          />
          <NavItem
            {...navItemProps}
            icon={<IconDownload />}
            text="Embedded analytics"
            to={INTERNAL_LINKS.EmbeddedAnalytics}
          />

          <NavItem
            {...navItemProps}
            icon={<IconMagic />}
            text="AI-powered"
            to={INTERNAL_LINKS.Magic}
          />
          <NavItem
            {...navItemProps}
            icon={<IconSemanticModels />}
            text="Trusted data"
            to={INTERNAL_LINKS.SemanticModels}
          />
          <NavItem
            {...navItemProps}
            icon={<IconForTeams />}
            text="Collaborative intelligence"
            to={INTERNAL_LINKS.ForTeams}
          />
          <NavItem
            {...navItemProps}
            icon={<IconExploratoryAnalysis />}
            text="Exploratory analysis"
            to={INTERNAL_LINKS.UseCaseExploratoryAnalysis}
          />
          <NavItem
            {...navItemProps}
            icon={<IconIntegrations />}
            text="Instant integration"
            to={INTERNAL_LINKS.Integrations}
          />

          <PurpleDivider />
          <HeadingDivider renderAs="h5">Solutions</HeadingDivider>
          <NavItem
            {...navItemProps}
            icon={<IconGroupedColumnChart />}
            text="Data leader"
            to={INTERNAL_LINKS.SolutionsDataLeaders}
          />
          <NavItem
            {...navItemProps}
            icon={<IconDataset />}
            text="Product"
            to={INTERNAL_LINKS.SolutionsProductAnalytics}
          />
          <NavItem
            {...navItemProps}
            icon={<IconMarketing />}
            text="Marketing"
            to={INTERNAL_LINKS.SolutionsMarketing}
          />
          <NavItem
            {...navItemProps}
            icon={<IconSales />}
            text="Sales"
            to={INTERNAL_LINKS.SolutionsSales}
          />
          <NavItem
            {...navItemProps}
            icon={<IconGroups />}
            text="Customer success"
            to={INTERNAL_LINKS.SolutionsCustomerSuccess}
          />
          <PurpleDivider />
          <HeadingDivider renderAs="h5">Resources</HeadingDivider>
          <NavItem
            {...navItemProps}
            icon={<IconTemplates />}
            text="Templates"
            to={INTERNAL_LINKS.Templates}
          />
          <NavItem
            {...navItemProps}
            icon={<IconAbout />}
            isExternal={true}
            text="Hex Foundations"
            to={DOCS_LINKS.HexFoundations}
          />
          <NavItem
            {...navItemProps}
            icon={<IconDocs />}
            isExternal={true}
            text="Docs"
            to={DOCS_LINKS.Home}
          />
          <NavItem
            {...navItemProps}
            icon={<IconChangelog />}
            isExternal={true}
            text="Changelog"
            to={DOCS_LINKS.Changelog}
          />
          <NavItem
            {...navItemProps}
            icon={<IconBlog />}
            text="Blog"
            to={INTERNAL_LINKS.Blog}
          />
          <NavItem
            {...navItemProps}
            icon={<IconPin />}
            text="Resources & Events"
            to={INTERNAL_LINKS.Resources}
          />
          <NavItem
            {...navItemProps}
            icon={<IconCustomers />}
            text="Customer stories"
            to={INTERNAL_LINKS.Customers}
          />
          <NavItem
            {...navItemProps}
            icon={<IconPartners />}
            text="Partners"
            to={INTERNAL_LINKS.Partners}
          />
          <PurpleDivider />
          <NavItem
            {...navItemProps}
            icon={<IconEnterprise />}
            text="Enterprise"
            to={INTERNAL_LINKS.Enterprise}
          />
          <NavItem
            {...navItemProps}
            icon={<IconPricing />}
            text="Pricing"
            to={INTERNAL_LINKS.Pricing}
          />
        </Main>
      </MobileMenu>
    </NavOptions>
  );
});
